/* eslint-disable class-methods-use-this */
import { Vue, Component, Watch } from 'vue-property-decorator'
import { namespace, Getter, State, Action } from 'vuex-class'
import {
  showNativeUI,
  createTransferPostSidePanel,
  createCopyPostSidePanel,
  createPostActionAlertSheet,
  createCommentMenuAlertSheet,
  PostActionMenuItem,
} from 'native_bridge/ui'
import { listen } from 'native_bridge/listen'
import * as sorter from 'surface/sorter'
import { CopyOrTransferPostResult } from 'surface/xstore/types'
import { createClickListener } from 'native_bridge/anchor'
import {
  openLink,
  editPost,
  expandPost,
  updateAddress,
  updateBackground,
  convertLayoutResult,
  saveSettingsResult,
  reportCopyPostResult,
  reportTransferPostResult,
} from 'native_bridge/actions'
import { ListenedMessageHandler } from 'native_bridge/types'
import nativeBridge from 'bits/native_bridge'
import postMessage from 'native_bridge/post_message'
import window from 'bits/global'
import { Post } from 'types'

type CopyOrTransferType = 'new' | 'current' | 'other'

const comment = namespace('comment')
const nativeApp = namespace('nativeApp')
const settings = namespace('settings')
const post = namespace('post')
const postAction = namespace('postAction')
const postConnection = namespace('postConnection')
const map = namespace('map')

@Component class NativeAppMixin extends Vue {
  @State private constants
  @State private wall
  @Getter private address
  @Getter private background
  @Getter private convertLayoutErrorMessage
  @Getter private format
  @Action private setCoverPost
  @Action private unsetCoverPost

  @comment.State private actionMenuCommentId
  @comment.Action private startEditingComment
  @comment.Action private deleteComment

  @settings.Getter private isSaving
  @settings.Getter private saveErrorMessage

  @map.Action private startPickingLocation

  @nativeApp.State public linkToOpen
  @nativeApp.State private expandedPost
  @nativeApp.State private alertSheetManifest
  @nativeApp.State private sidePanelManifest
  @nativeApp.Action private clearOpenLink

  @post.Getter private postsArray
  @post.Getter private postBeingEdited
  @post.Action private startEditingPost
  @post.Action private deletePost
  @post.Action private sortedPost
  @post.Action private removePostColor
  @post.Action private changePostColor

  @postAction.State private copyOrTransferPostResult
  @postAction.Getter private postUnderAction
  @postAction.Getter private canEditPostUnderAction
  @postAction.Getter private canConnectPostUnderAction
  @postAction.Getter private canDisconnectPostUnderAction
  @postAction.Getter private canChangePostStackOrder
  @postAction.Getter private canTransferPostUnderAction
  @postAction.Getter private canCopyPostUnderAction
  @postAction.Getter private canChangeCoverToPostUnderAction
  @postAction.Getter private canChangePostUnderActionLocation
  @postAction.Getter private isPostUnderActionPreviewPost
  @postAction.Getter private canDeletePostUnderAction
  @postAction.Action private expandPost
  @postAction.Action private startPostTransfer
  @postAction.Action private startPostCopy
  @postAction.Action private hidePostActionMenu

  @postConnection.Action private startConnection
  @postConnection.Action private startDisconnection

  @Watch('address')
  public respondToAddressChange (newValue: string, oldValue: string): void {
    postMessage(updateAddress(newValue))
  }

  @Watch('linkToOpen')
  public respondToLinkToOpenChange (newValue, oldValue): void {
    if (newValue && !oldValue) {
      const { linkType, url, contentCategory, contentSubcategory } = newValue
      postMessage(openLink(url, linkType, contentCategory, contentSubcategory))
      this.clearOpenLink()
    }
  }

  @Watch('expandedPost')
  private respondToSettingExpandedPostManifest (newExpandedPost): void {
    if (!newExpandedPost) return
    const { wishFrameUrlTemplate } = this.constants
    postMessage(
      expandPost({
        expandedPost: newExpandedPost,
        allPosts: this.postsArray,
        wall: this.wall,
        window,
        wishFrameUrlTemplate,
      })
    )
  }

  @Watch('format')
  public respondToLayoutChange (): void {
    postMessage(convertLayoutResult({ status: 'success' }))
  }

  @Watch('convertLayoutErrorMessage')
  public respondToLayoutChangeError (errorMessage): void {
    if (errorMessage) {
      postMessage(convertLayoutResult({
        status: 'error',
        error_reason: errorMessage,
      }))
    }
  }

  @Watch('background')
  public respondToBackgroundColorChange (newValue): void {
    postMessage(updateBackground(newValue))
  }

  @Watch('actionMenuCommentId')
  private respondToActionMenuCommentId (actionMenuCommentId: number | null): void {
    if (!actionMenuCommentId) return
    this.listenToCommentActionMenu(actionMenuCommentId)
    showNativeUI(createCommentMenuAlertSheet(actionMenuCommentId))
  }

  private listenToCommentActionMenu (commentUnderActionId: number): void {
    listen('edit_comment', (): void => {  this.startEditingComment({ commentId: commentUnderActionId }) })
    listen('delete_comment', (): void => { this.deleteComment({ commentId: commentUnderActionId }) })
  }

  @Watch('postBeingEdited')
  private respondToPostBeingEditedChange (currentPost: Post, lastPost: Post): void {
    if (!currentPost) return
    if (!lastPost || currentPost.cid !== lastPost.cid) {
      postMessage(editPost(currentPost))
    }
  }

  @Watch('isSaving')
  public respondToSettingsSave (isSaving: boolean, wasSaving: boolean): void {
    const justSaved = !isSaving && wasSaving
    if (!justSaved) return
    let result: { [key: string]: unknown } = {}
    if (this.saveErrorMessage) {
      result = {
        status: 'error',
        error_reason: this.saveErrorMessage,
      }
      nativeBridge.trigger.saveSettingsFail(this.saveErrorMessage)
    } else {
      result = {
        status: 'success',
        wall: this.wall,
      }
      nativeBridge.trigger.saveSettingsSuccess()
    }
    postMessage(saveSettingsResult(result))
  }

  @Watch('postUnderAction')
  private respondToPostUnderAction (postUnderAction: Post): void {
    if (!postUnderAction) return
    // 1. Listen to events
    this.listenToPostUnderActionMenu(postUnderAction)
    // 2. create the postUnderAction action menu manifest here and show it
    const wall = {
      publicKey: this.wall.public_key,
      themeColors: this.constants.themeColor,
      mobileAppCopyPostUrlTemplate: this.constants.mobileAppCopyPostUrlTemplate,
      mobileAppTransferPostUrlTemplate: this.constants.mobileAppTransferPostUrlTemplate,
    }

    // Specify which options will appear in the alert sheet.
    const sheetOptions: PostActionMenuItem[] = []
    if (this.canEditPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Colors)
      sheetOptions.push(PostActionMenuItem.Edit)
    }
    if (this.canChangePostUnderActionLocation) {
      sheetOptions.push(PostActionMenuItem.ChangeLocation)
    }
    if (this.canConnectPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Connect)
    }
    if (this.canDisconnectPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Disconnect)
    }
    if (this.canChangePostStackOrder) {
      sheetOptions.push(PostActionMenuItem.BringPostToFront)
      sheetOptions.push(PostActionMenuItem.SendPostToBack)
    }
    if (this.canEditPostUnderAction || this.canConnectPostUnderAction || this.canDisconnectPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Divider)
    }
    if (this.canTransferPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Transfer)
    }
    if (this.canCopyPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Copy)
    }
    sheetOptions.push(PostActionMenuItem.Expand)
    if (this.canChangeCoverToPostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Divider)
      if (this.isPostUnderActionPreviewPost) {
        sheetOptions.push(PostActionMenuItem.UnsetAsCover)
      } else {
        sheetOptions.push(PostActionMenuItem.SetAsCover)
      }
    }
    if (this.canDeletePostUnderAction) {
      sheetOptions.push(PostActionMenuItem.Divider)
      sheetOptions.push(PostActionMenuItem.Delete)
    }

    const { id: postId, cid: postCid, author_id: postAuthorId } = postUnderAction
    const id = Number(postId)
    const cid = postCid as string
    const author_id = Number(postAuthorId)

    const alertSheet = createPostActionAlertSheet(
      { id, cid, author_id },
      wall,
      sheetOptions,
    )
    showNativeUI(alertSheet)
  }

  private listenToPostUnderActionMenu (postUnderAction: Post): void {
    const postId = Number(postUnderAction.id)
    const postCid = postUnderAction.cid
    const authorId = Number(postUnderAction.author_id)
    const hideMenuFirst = (wrapped: Function): ListenedMessageHandler => {
      return (msg: unknown): void => {
        this.hidePostActionMenu()
        wrapped(msg)
      }
    }
    listen('edit_post', hideMenuFirst((): void => { this.startEditingPost({ postCid }) }))
    listen('connect_post', hideMenuFirst((): void => { this.startConnection({ postId }) }))
    listen('disconnect_post', hideMenuFirst((): void => { this.startDisconnection({ postId }) }))
    listen('bring_post_to_front', hideMenuFirst((): void => { this.sortedPost({ postCid, sortIndex: sorter.getTopSortIndex() }) }))
    listen('send_post_to_back', hideMenuFirst((): void => { this.sortedPost({ postCid, sortIndex: sorter.getBottomSortIndex() }) }))
    listen('expand_post', hideMenuFirst((): void => { this.expandPost({ postCid }) }))
    listen('delete_post', hideMenuFirst((): void => { this.deletePost({ postCid }) }))
    listen('update_post_color', hideMenuFirst((msg): void => { this.changePostColor({ postCid, newBgColor: msg.label }) }))
    listen('remove_post_color', hideMenuFirst((): void => { this.removePostColor({ postCid }) }))
    listen('set_as_cover', hideMenuFirst((): void => { this.setCoverPost({ postId }) }))
    listen('unset_as_cover', hideMenuFirst((): void => { this.unsetCoverPost({ postId }) }))
    listen('change_location', hideMenuFirst((): void => { this.startPickingLocation({ postCid }) }))
    listen('transfer_post', hideMenuFirst((): void => {
      // track TODO
      showNativeUI(
        createTransferPostSidePanel(
          this.constants.mobileAppTransferPostUrlTemplate,
          this.wall,
          { id: postId, author_id: authorId }
        )
      )
      this.startPostTransfer({ transferPostCid: postCid })
    }))
    listen('copy_post', (): void => {
      // track TODO
      showNativeUI(
        createCopyPostSidePanel(
          this.constants.mobileAppCopyPostUrlTemplate,
          this.wall,
          { id: postId, author_id: authorId }
        )
      )
      this.startPostCopy({ copyPostCid: postCid })
    })
  }

  @Watch('copyOrTransferPostResult')
  private respondToCopyingOrTransferringPost (copyOrTransferPostResult: CopyOrTransferPostResult): void {
    if (!copyOrTransferPostResult) return
    const { isDestinationNewWall, isDestinationThisWall, status, url, text, operation } = copyOrTransferPostResult
    let actionType: CopyOrTransferType = 'other'
    if (isDestinationNewWall) {
      actionType = 'new'
    } else if (isDestinationThisWall) {
      actionType = 'current'
    }
    if (operation === 'copy') {
      postMessage(reportCopyPostResult({
        copy_type: actionType,
        status,
        url,
        text,
      }))
    } else if (operation === 'transfer') {
      postMessage(reportTransferPostResult({
        transfer_type: actionType,
        status,
        url,
        text,
      }))
    } else {
      throw new Error(`Unknown operation: ${operation}`)
    }
  }

  private mounted (): void {
    // Make all body anchor tags listen to click events and
    // respond to native app
    document.body.addEventListener('click', createClickListener(this.$store))
  }
}

export default NativeAppMixin
